'use client'

import { capitalize, map } from 'lodash'
import { useCallback, useEffect, useState } from 'react'

import {
  addDays,
  addMonths,
  format,
  getDaysInMonth,
  getMonth,
  getYear,
  startOfDay,
  subMonths,
} from 'date-fns'

import { ptBR } from 'date-fns/locale/pt-BR'

import { BiChevronLeft, BiChevronRight } from 'react-icons/bi'

import { Order } from '@/@types'
import { Button } from '@/components/Button'
import { SquircleAdapter } from '@/components/SquircleAdapter'
import { MIN_NOTICE_DAYS } from '@/config/constants'
import { useBreakpoints } from '@/hooks'

import { useCakeMakerActions, useCakeMakerState } from '@/store/useCakeMaker'

import './styles.scss'

type CalendarStepProps = {
  orders: Order[]
}

const DAYS_ALIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

const DAYS_FULL_NAME = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
]

const CURRENT_DATE = new Date()

export function CalendarStep({ orders }: CalendarStepProps) {
  const { isSmall } = useBreakpoints()
  const { orderDate } = useCakeMakerState()
  const { setOrderDate } = useCakeMakerActions()
  const [date, setDate] = useState(startOfDay(CURRENT_DATE))

  const isDateToday = (dateToCheck: Date) => {
    return (
      dateToCheck.getDate() === CURRENT_DATE.getDate() &&
      dateToCheck.getMonth() === CURRENT_DATE.getMonth() &&
      dateToCheck.getFullYear() === CURRENT_DATE.getFullYear()
    )
  }

  const isCurrentDate = () => {
    return (
      getMonth(date) === getMonth(CURRENT_DATE) &&
      getYear(date) === getYear(CURRENT_DATE)
    )
  }

  const onSubtractMonth = () => {
    if (isCurrentDate()) return
    const newDate = subMonths(date, 1)
    setDate(newDate)
  }

  const onAddMonth = () => {
    const newDate = addMonths(date, 1)
    setDate(newDate)
  }

  const getFirstDayOfMonth = () => {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1)
    return firstDayOfMonth.getDay()
  }

  const isDayImpracticable = (dayDate: Date) => {
    const threeDaysAfterCurrentDate = addDays(CURRENT_DATE, MIN_NOTICE_DAYS)
    return dayDate <= threeDaysAfterCurrentDate
  }

  const isDayBusy = useCallback(
    (dayDate: Date) => {
      const formattedDateToCompare = format(dayDate, 'dd/MM/yyyy')
      return map(orders, 'date').includes(formattedDateToCompare)
    },
    [orders],
  )

  const isDaySelected = (dayDate: Date) => {
    return orderDate && dayDate.getTime() === orderDate.getTime()
  }

  useEffect(() => {
    let available = false
    for (let day = 1; day <= getDaysInMonth(date); day++) {
      const dayDate = new Date(getYear(date), getMonth(date), day, 12, 0, 0)
      if (!isDayImpracticable(dayDate) && !isDayBusy(dayDate)) {
        available = true
        break
      }
    }

    if (!available) {
      setDate(addMonths(date, 1))
    }
  }, [orders, isDayBusy])

  const isCurrentYear = getYear(date) === getYear(CURRENT_DATE)

  return (
    <div className="calendar-step">
      <div className="calendar-step__date-navigation">
        <div className="arrows">
          <Button
            onClick={onSubtractMonth}
            variant="primary"
            rightRender={<BiChevronLeft />}
            disabled={isCurrentDate()}
            aria-label="Mês anterior"
          />

          <Button
            onClick={onAddMonth}
            variant="primary"
            rightRender={<BiChevronRight />}
            aria-label="Próximo mês"
          />
        </div>

        <p className="current-month" aria-live="polite">
          {isCurrentYear
            ? capitalize(format(date, 'MMMM', { locale: ptBR }))
            : capitalize(format(date, 'MMMM, yyyy', { locale: ptBR }))}
        </p>
      </div>

      <div className="calendar-step__header">
        {DAYS_ALIAS.map((dayAlias, idx) => {
          const formattedAlias = isSmall ? dayAlias.substring(0, 1) : dayAlias
          return (
            <p key={dayAlias} className="day-alias">
              <span aria-hidden>{formattedAlias}</span>
              <span className="sr-only">{DAYS_FULL_NAME[idx]}</span>
            </p>
          )
        })}
      </div>

      <div className="calendar-step__body">
        {new Array(getFirstDayOfMonth()).fill('').map((_, idx) => (
          <div key={`empty-${idx}`} />
        ))}

        {new Array(getDaysInMonth(date)).fill('').map((_, idx) => {
          const day = idx + 1
          const dayDate = new Date(getYear(date), getMonth(date), day, 12, 0, 0)

          const today = isDateToday(dayDate)

          const impracticable = isDayImpracticable(dayDate)
          const busy = !impracticable && isDayBusy(dayDate)
          const disabled = impracticable || busy
          const selected = isDaySelected(dayDate)

          const fullDate = format(dayDate, "d 'de' MMMM 'de' yyyy", {
            locale: ptBR,
          })

          const dayLabel = [
            fullDate,
            today && 'hoje',
            impracticable &&
              `indisponível, exige ${MIN_NOTICE_DAYS} dias de antecedência`,
            busy && 'indisponível, agenda cheia',
          ]
            .filter(Boolean)
            .join(', ')

          return (
            <button
              key={`${getYear(date)}-${getMonth(date)}-${day}-${today.toString()}`}
              disabled={disabled}
              data-today={today}
              onClick={() => !disabled && setOrderDate(dayDate)}
              aria-label={dayLabel}
              aria-pressed={selected || false}
            >
              <SquircleAdapter
                cornerRadius={isSmall ? 16 : 24}
                className="day-container"
                data-disabled={disabled}
                data-selected={selected}
                data-today={today}
              >
                <p className="day-number" aria-hidden>
                  {day}
                </p>
              </SquircleAdapter>
            </button>
          )
        })}
      </div>

      <p className="calendar-step__legend">
        Produzimos cada bolo com no mínimo {MIN_NOTICE_DAYS} dias de
        antecedência. Datas apagadas estão dentro desse prazo ou com a agenda
        cheia.
      </p>
    </div>
  )
}
